import { VEHICLE_CATALOG_ENTRY_STATUSES, VEHICLE_CATALOG_SOURCES } from '@carcheck/shared';
import { z } from 'zod';

import type { CatalogMakesParams } from '../../../api/admin-vehicle-catalog';

/**
 * URL filter schema for `/vehicle-catalog` (AUT-437). Mirrors the API's
 * `ListMakesQuerySchema`: `q` (name or make alias, trimmed, ≤ 50 — the API
 * cap), `status` (all | active | hidden), `onSale` (any | true | false),
 * `source` (all | seed | staff | suggestion). Junk degrades to the default.
 *
 * Not here: the mockup's «Без видимых моделей» toggle — no server filter.
 */
export const SEARCH_MAX = 50;

export const STATUS_OPTIONS = ['all', ...VEHICLE_CATALOG_ENTRY_STATUSES] as const;
export const ON_SALE_OPTIONS = ['any', 'true', 'false'] as const;
export const SOURCE_OPTIONS = ['all', ...VEHICLE_CATALOG_SOURCES] as const;

export const catalogFiltersSchema = z.object({
  q: z
    .string()
    .transform((v) => v.trim().slice(0, SEARCH_MAX))
    .optional()
    .catch(undefined),
  status: z.enum(STATUS_OPTIONS).catch('all').default('all'),
  onSale: z.enum(ON_SALE_OPTIONS).catch('any').default('any'),
  source: z.enum(SOURCE_OPTIONS).catch('all').default('all'),
});

export type CatalogFilters = z.infer<typeof catalogFiltersSchema>;

export const hasActiveFilters = (f: CatalogFilters): boolean =>
  Boolean(f.q) || f.status !== 'all' || f.onSale !== 'any' || f.source !== 'all';

/** A stable string of the filters: a change resets the cursor stack. */
export const filterKeyOf = (f: CatalogFilters): string =>
  [f.q ?? '', f.status, f.onSale, f.source].join('|');

export const toListParams = (
  f: CatalogFilters,
  cursor: string | null,
  limit: number,
): CatalogMakesParams => ({
  status: f.status,
  ...(f.q ? { q: f.q } : {}),
  ...(f.onSale === 'any' ? {} : { onSale: f.onSale === 'true' }),
  ...(f.source === 'all' ? {} : { source: f.source }),
  cursor,
  limit,
});
